import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import {
  FC, useEffect, useMemo, useState,
} from 'react';
import Grid from '@mui/material/Grid2';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDorIntegration } from '../../../store/settings/selectors';
import { DorStats, FootTrafficChart } from './components';
import { getLocationMetrics } from '../../../store/orders/selectors';
import { MemoizedFilterTabs } from '../../../components/base';
import { getMembers } from '../../../store/team/selectors';
import { DorLocationsDialog } from '../../settings/components/DorLocationsDialog';
import { INDEX_TO_COLOR } from '../../../data';

type TDorAnalytics = {
  locationId: string
};

const DorAnalytics: FC<TDorAnalytics> = ({ locationId }) => {
  const locationMetrics = useAppSelector(getLocationMetrics);

  const dorIntegration = useAppSelector(getDorIntegration);
  const members = useAppSelector(getMembers);

  const [filterId, setFilterId] = useState<number>(Number(locationId));

  useEffect(() => {
    setFilterId(Number(locationId));
  }, [locationId]);

  const getLengthByStatus = (locationId: number) => members.reduce(
    (count, item) => (item.locations.some(({ id }) => id === locationId) ? count + 1 : count),
    0,
  );

  const tabs = useMemo(() => {
    if (dorIntegration?.locationsMapping) {
      return dorIntegration.locationsMapping?.map((location, index) => ({
        value: Number(location.shopifyLocationId),
        label: location.shopifyLocationName,
        color: INDEX_TO_COLOR[index],
        count: getLengthByStatus(Number(location.shopifyLocationId)),
      }));
    }

    return [];
  }, [dorIntegration]);

  const handleFilterStatus = (newValue: number) => {
    setFilterId(newValue);
  };

  const dataByLocationId = useMemo(() => {
    if (filterId !== 0) {
      const selectedLocation = dorIntegration?.locationsMapping?.find((location) => location.shopifyLocationId === filterId?.toString());

      return selectedLocation?.dorLocationId ? locationMetrics?.[selectedLocation?.dorLocationId] : [];
    }
    return [];
  }, [filterId, locationMetrics, dorIntegration, locationId]);

  return (
    <Container
      sx={{
        px: { xs: 1, lg: 2 },
      }}
      maxWidth={false}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {locationId && (
        <MemoizedFilterTabs
          tabs={tabs}
          initialFilter={Number(locationId)}
          onFilterChange={handleFilterStatus}
          isDisplayBackground={false}
        />
        )}
      </Stack>
      <Grid container spacing={2}>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <DorStats data={dataByLocationId || []} />
        </Grid>
        <Grid
          pt={2}
          size={{
            sm: 12, xs: 12, md: 12, lg: 12,
          }}
        >
          <FootTrafficChart data={dataByLocationId || []} locationId={filterId} />
        </Grid>
      </Grid>
      <DorLocationsDialog />
    </Container>
  );
};

export {
  DorAnalytics,
};
